import React, { useEffect, useMemo, useState } from "react";
import API_URL from "../apiConfig";

function getTokenFromUrl() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("token") || "";
}

function getStatusLabel(status) {
  if (status === "active") return "Activa";
  if (status === "unsubscribed") return "Dada de baja";
  return "—";
}

function getStatusText(status) {
  if (status === "active") {
    return "Tu suscripción a ZEDRO News está activa. Seguirás recibiendo nuevas ediciones cuando sean enviadas.";
  }

  if (status === "unsubscribed") {
    return "Esta suscripción está dada de baja. No recibirás nuevos envíos de ZEDRO News mientras permanezca en este estado.";
  }

  return "No pudimos confirmar el estado actual de esta suscripción.";
}

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("es-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(value));
  } catch {
    return "—";
  }
}

export default function PreferencesPage() {
  const token = useMemo(() => getTokenFromUrl(), []);

  const [subscriber, setSubscriber] = useState(null);
  const [language, setLanguage] = useState("es");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadPreferences() {
      if (!token) {
        setLoading(false);
        setError("El enlace de preferencias no es válido o está incompleto.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${API_URL}/api/subscribers/preferences?token=${encodeURIComponent(token)}`
        );

        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || "preferences_not_found");
        }

        if (!cancelled) {
          setSubscriber(json.subscriber);
          setLanguage(json.subscriber?.language === "fr" ? "fr" : "es");
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError("No pudimos cargar tus preferencias. El enlace puede ser inválido o haber expirado.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function updatePreferences(action = "update") {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      const res = await fetch(`${API_URL}/api/subscribers/preferences`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          language,
          action,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "preferences_update_failed");
      }

      setSubscriber(json.subscriber);

      if (action === "unsubscribe") {
        setMessage("Tu baja fue registrada correctamente. No recibirás nuevos envíos de ZEDRO News.");
      } else if (action === "resubscribe") {
        setMessage("Tu suscripción fue reactivada correctamente.");
      } else {
        setMessage("Tus preferencias fueron actualizadas correctamente.");
      }
    } catch (err) {
      console.error(err);
      setError("No pudimos actualizar tus preferencias. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main style={styles.page}>
      <section style={styles.hero}>
        <span style={styles.kicker}>ZEDRO News</span>
        <h1 style={styles.title}>Preferencias</h1>
        <p style={styles.subtitle}>
          Gestiona el idioma y el estado de tu suscripción a ZEDRO News.
        </p>
      </section>

      <section style={styles.card}>
        {loading && <p style={styles.text}>Cargando preferencias…</p>}

        {!loading && error && (
          <div style={styles.errorBox}>{error}</div>
        )}

        {!loading && !error && subscriber && (
          <>
            <div style={styles.statusRow}>
              <div>
                <p style={styles.label}>Correo</p>
                <p style={styles.email}>{subscriber.email}</p>
              </div>

              <span
                style={{
                  ...styles.statusBadge,
                  ...(subscriber.status === "active"
                    ? styles.statusActive
                    : styles.statusInactive),
                }}
              >
                {getStatusLabel(subscriber.status)}
              </span>
            </div>

            <p style={styles.statusText}>{getStatusText(subscriber.status)}</p>

            <div style={styles.metaGrid}>
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Alta</span>
                <strong>{formatDate(subscriber.created_at)}</strong>
              </div>

              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Consentimiento</span>
                <strong>{formatDate(subscriber.consent_at)}</strong>
              </div>

              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Baja</span>
                <strong>{formatDate(subscriber.unsubscribed_at)}</strong>
              </div>
            </div>

            <label style={styles.fieldLabel}>
              Idioma preferido
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                style={styles.select}
                disabled={saving}
              >
                <option value="es">Español</option>
                <option value="fr">Français</option>
              </select>
            </label>

            <div style={styles.actions}>
              <button
                type="button"
                onClick={() => updatePreferences("update")}
                disabled={saving}
                style={styles.primaryButton}
              >
                {saving ? "Guardando…" : "Guardar preferencias"}
              </button>

              {subscriber.status === "active" ? (
                <button
                  type="button"
                  onClick={() => updatePreferences("unsubscribe")}
                  disabled={saving}
                  style={styles.secondaryButton}
                >
                  Darme de baja
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => updatePreferences("resubscribe")}
                  disabled={saving}
                  style={styles.secondaryButton}
                >
                  Reactivar suscripción
                </button>
              )}
            </div>

            {message && <div style={styles.successBox}>{message}</div>}
          </>
        )}
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "60vh",
    padding: "52px 18px 72px",
    background: "linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)",
    color: "#0f2f46",
  },
  hero: {
    width: "min(860px, 100%)",
    margin: "0 auto 22px",
  },
  kicker: {
    display: "inline-flex",
    minHeight: 28,
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 999,
    background: "rgba(212,176,98,.13)",
    border: "1px solid rgba(212,176,98,.32)",
    color: "#9d7d2c",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
  },
  title: {
    margin: "14px 0 8px",
    fontSize: "clamp(34px, 6vw, 54px)",
    lineHeight: 1.04,
    letterSpacing: "-.05em",
    color: "#0f2f46",
  },
  subtitle: {
    margin: 0,
    maxWidth: 640,
    color: "rgba(31,47,64,.70)",
    fontSize: 17,
    lineHeight: 1.65,
  },
  card: {
    width: "min(860px, 100%)",
    margin: "0 auto",
    padding: 24,
    borderRadius: 26,
    background: "#ffffff",
    border: "1px solid rgba(44,59,81,.10)",
    boxShadow: "0 18px 48px rgba(15,47,70,.08)",
    boxSizing: "border-box",
  },
  text: {
    margin: 0,
    color: "rgba(31,47,64,.72)",
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 14,
  },
  label: {
    margin: "0 0 5px",
    color: "#b99a43",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: ".10em",
    textTransform: "uppercase",
  },
  email: {
    margin: 0,
    fontSize: 22,
    color: "#0f2f46",
    fontWeight: 800,
    overflowWrap: "anywhere",
  },
  statusBadge: {
    minHeight: 30,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  statusActive: {
    color: "#116b34",
    background: "#e9fbef",
    border: "1px solid #bdebc9",
  },
  statusInactive: {
    color: "#526173",
    background: "#f4f6f8",
    border: "1px solid #d9e2ec",
  },
  statusText: {
    margin: "0 0 18px",
    color: "rgba(31,47,64,.72)",
    lineHeight: 1.65,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  metaItem: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(44,59,81,.09)",
    background: "#f8fafc",
    display: "grid",
    gap: 4,
    color: "#0f2f46",
  },
  metaLabel: {
    color: "rgba(31,47,64,.58)",
    fontSize: 12,
    fontWeight: 800,
  },
  fieldLabel: {
    display: "grid",
    gap: 8,
    color: "rgba(31,47,64,.75)",
    fontSize: 13,
    fontWeight: 900,
    marginBottom: 18,
  },
  select: {
    width: "100%",
    minHeight: 46,
    borderRadius: 14,
    border: "1px solid rgba(44,59,81,.16)",
    background: "#fff",
    color: "#0f2f46",
    padding: "0 13px",
    fontSize: 15,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  primaryButton: {
    minHeight: 44,
    padding: "0 18px",
    border: 0,
    borderRadius: 999,
    background: "#0f2f46",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryButton: {
    minHeight: 44,
    padding: "0 18px",
    borderRadius: 999,
    border: "1px solid rgba(44,59,81,.16)",
    background: "#fff",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
  },
  successBox: {
    marginTop: 16,
    padding: 14,
    borderRadius: 14,
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#116b34",
    lineHeight: 1.55,
  },
  errorBox: {
    padding: 14,
    borderRadius: 14,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    lineHeight: 1.55,
  },
};
